import { useCallback, useEffect, useState } from "react";
import {
  PAGES,
  PermissionEntry,
  RoleWithPermissions,
  createRole,
  fetchAllRolesAdmin,
  removeRoleFromEmployee,
  setRolePermissions,
  updateRole,
} from "features/roles/api/RolesApi";
import { Key, Pencil, Plus, RotateCcw, Trash2, X } from "lucide-react";
import ConfirmModal from "shared/ConfirmModal";

const PAGE_LABELS: Record<string, string> = {
  employees: "Employees",
  finance: "Finance",
  inventory: "Inventory",
  teams: "Teams",
  attendance: "Attendance",
  evaluation: "Employee Evaluation",
  cv_processor: "CV Processor",
  interview_qa: "Interview Q&A",
  job_board: "Job Board",
  hiring_contacts: "LinkedIn Hiring Contacts",
  prompt_library: "Prompt Library",
  crm: "SLED CRM",
  meetings: "Employee Meetings",
};

export default function RolesManagement() {
  const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  // Permissions panel state
  const [permissionsRole, setPermissionsRole] = useState<RoleWithPermissions | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<RoleWithPermissions | null>(null);
  const [permissionsDraft, setPermissionsDraft] = useState<Record<string, { can_view: boolean; can_edit: boolean }>>({});
  const [permissionsSaving, setPermissionsSaving] = useState(false);

  const load = useCallback(async () => {
    setError("");
    const data = await fetchAllRolesAdmin();
    setRoles(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await load();
      } catch (e: any) {
        const detail = e.response?.data?.detail;
        if (!cancelled) {
          setError(typeof detail === "string" ? detail : "Failed to load roles");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) { setError("Enter a role name"); return; }
    setSaving(true);
    setError("");
    try {
      await createRole(name);
      setNewName("");
      await load();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not create role");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (role: RoleWithPermissions) => {
    setEditingId(role.id);
    setEditName(role.role_name);
    setError("");
  };

  const cancelEdit = () => { setEditingId(null); setEditName(""); };

  const saveEdit = async (roleId: number) => {
    const name = editName.trim();
    if (!name) { setError("Role name cannot be empty"); return; }
    setSaving(true);
    setError("");
    try {
      await updateRole(roleId, { role_name: name });
      cancelEdit();
      await load();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not update role");
    } finally {
      setSaving(false);
    }
  };

  const requestDeactivate = (role: RoleWithPermissions) => setPendingDeactivate(role);
  const cancelDeactivate = () => setPendingDeactivate(null);

  const confirmDeactivate = async () => {
    const role = pendingDeactivate;
    if (!role) return;
    setSaving(true);
    setError("");
    try {
      await removeRoleFromEmployee(role.id);
      cancelEdit();
      if (permissionsRole?.id === role.id) setPermissionsRole(null);
      await load();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not delete role");
    } finally {
      setSaving(false);
      setPendingDeactivate(null);
    }
  };

  const handleReactivate = async (role: RoleWithPermissions) => {
    setSaving(true);
    setError("");
    try {
      await updateRole(role.id, { is_active: true });
      await load();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reactivate role");
    } finally {
      setSaving(false);
    }
  };

  const openPermissionsPanel = (role: RoleWithPermissions) => {
    if (permissionsRole?.id === role.id) {
      setPermissionsRole(null);
      return;
    }
    const draft: Record<string, { can_view: boolean; can_edit: boolean }> = {};
    PAGES.forEach((p) => { draft[p] = { can_view: false, can_edit: false }; });
    role.permissions.forEach((perm) => {
      // A page the backend no longer offers (e.g. a legacy "sheets" row
      // from before that permission was removed) must never be merged in —
      // it wouldn't render as a checkbox to un-toggle, yet would still ride
      // along in the save payload and get flatly rejected by the backend's
      // page-validity check, blocking the save of every OTHER permission
      // on this role too.
      if (!(PAGES as readonly string[]).includes(perm.page)) return;
      draft[perm.page] = { can_view: perm.can_view, can_edit: perm.can_edit };
    });
    setPermissionsDraft(draft);
    setPermissionsRole(role);
  };

  const togglePermission = (page: string, field: "can_view" | "can_edit") => {
    setPermissionsDraft((prev) => {
      const current = prev[page] || { can_view: false, can_edit: false };
      const updated = { ...current, [field]: !current[field] };
      // enabling edit implies view; disabling view implies no edit
      if (field === "can_edit" && updated.can_edit) updated.can_view = true;
      if (field === "can_view" && !updated.can_view) updated.can_edit = false;
      return { ...prev, [page]: updated };
    });
  };

  const handleSavePermissions = async () => {
    if (!permissionsRole) return;
    setPermissionsSaving(true);
    setError("");
    try {
      const permissions: PermissionEntry[] = Object.entries(permissionsDraft)
        .filter(([, v]) => v.can_view || v.can_edit)
        .map(([page, v]) => ({ page, can_view: v.can_view, can_edit: v.can_edit }));
      await setRolePermissions(permissionsRole.id, permissions);
      setPermissionsRole(null);
      await load();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not save permissions");
    } finally {
      setPermissionsSaving(false);
    }
  };

  return (
    <>
    {pendingDeactivate && (
      <ConfirmModal
        title="Deactivate role"
        message={`Deactivate role "${pendingDeactivate.role_name}"? It will be removed from all employees.`}
        confirmLabel="Deactivate"
        variant="danger"
        confirming={saving}
        onConfirm={confirmDeactivate}
        onCancel={cancelDeactivate}
      />
    )}
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground font-inter">Roles</h1>
        <p className="text-sm text-muted-foreground font-inter mt-1">
          Create, rename, or deactivate roles and assign page permissions to each.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-surface/50 p-5 mb-8">
        <h2 className="text-sm font-medium text-foreground font-inter mb-3">Add role</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. HR, Team Lead"
            disabled={saving}
            className="flex-1 px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving || !newName.trim()}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm font-inter">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-border overflow-hidden mb-6">
            <table className="w-full text-left text-sm font-inter">
              <thead className="bg-surface-muted/80 text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium w-28">Status</th>
                  <th className="px-4 py-3 font-medium w-52 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                      No roles yet. Add one above.
                    </td>
                  </tr>
                ) : (
                  roles.map((role) => (
                    <tr
                      key={role.id}
                      className={`hover:bg-surface-muted/40 transition-colors ${
                        permissionsRole?.id === role.id ? "bg-primary/5" : "bg-surface/30"
                      }`}
                    >
                      <td className="px-4 py-3 text-foreground">
                        {editingId === role.id ? (
                          <input
                            type="text"
                            aria-label="Role name"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            disabled={saving}
                            className="w-full max-w-xs px-2 py-1.5 bg-surface-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                          />
                        ) : (
                          role.role_name
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-lg text-xs font-medium ${
                            role.is_active
                              ? "bg-emerald-500/15 text-emerald-400"
                              : "bg-surface-muted text-muted-foreground"
                          }`}
                        >
                          {role.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {editingId === role.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => saveEdit(role.id)}
                                disabled={saving}
                                className="px-2 py-1.5 text-xs font-medium text-primary hover:text-primary disabled:opacity-50"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                disabled={saving}
                                className="px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              {role.is_active ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openPermissionsPanel(role)}
                                    disabled={saving}
                                    className={`p-2 transition-colors disabled:opacity-50 ${
                                      permissionsRole?.id === role.id
                                        ? "text-primary"
                                        : "text-muted-foreground hover:text-primary"
                                    }`}
                                    title="Edit permissions"
                                  >
                                    <Key className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(role)}
                                    disabled={saving}
                                    className="p-2 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                                    title="Edit name"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => requestDeactivate(role)}
                                    disabled={saving}
                                    className="p-2 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                                    title="Deactivate"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleReactivate(role)}
                                  disabled={saving}
                                  className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-emerald-400 hover:text-emerald-300 disabled:opacity-50"
                                  title="Reactivate"
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                  Reactivate
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Permissions panel */}
          {permissionsRole && (
            <div className="rounded-2xl border border-primary/30 bg-surface/50 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div>
                  <h2 className="text-sm font-semibold text-foreground font-inter">
                    Page Permissions
                  </h2>
                  <p className="text-xs text-muted-foreground font-inter mt-0.5">
                    Role: <span className="text-primary">{permissionsRole.role_name}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPermissionsRole(null)}
                  className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Close permissions panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 py-4">
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-8 gap-y-0">
                  <div className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wider pb-2">
                    Page
                  </div>
                  <div className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wider pb-2 text-center w-16">
                    View
                  </div>
                  <div className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wider pb-2 text-center w-16">
                    Edit
                  </div>

                  {PAGES.map((page) => {
                    const perm = permissionsDraft[page] || { can_view: false, can_edit: false };
                    return (
                      <>
                        <div key={`${page}-label`} className="py-2.5 flex items-center text-sm text-foreground font-inter border-t border-border/60">
                          {PAGE_LABELS[page] || page}
                        </div>
                        <div key={`${page}-view`} className="py-2.5 flex items-center justify-center border-t border-border/60">
                          <input
                            type="checkbox"
                            aria-label={`${PAGE_LABELS[page]} view`}
                            checked={perm.can_view}
                            onChange={() => togglePermission(page, "can_view")}
                            disabled={permissionsSaving}
                            className="w-4 h-4 rounded border-border bg-surface-muted text-primary focus:ring-ring focus:ring-offset-background disabled:opacity-50 cursor-pointer"
                          />
                        </div>
                        <div key={`${page}-edit`} className="py-2.5 flex items-center justify-center border-t border-border/60">
                          {page === "cv_processor" || page === "hiring_contacts" ? (
                            <span className="text-xs text-muted-foreground font-inter">—</span>
                          ) : (
                          <input
                            type="checkbox"
                            aria-label={`${PAGE_LABELS[page]} edit`}
                            checked={perm.can_edit}
                            onChange={() => togglePermission(page, "can_edit")}
                            disabled={permissionsSaving}
                            className="w-4 h-4 rounded border-border bg-surface-muted text-primary focus:ring-ring focus:ring-offset-background disabled:opacity-50 cursor-pointer"
                          />
                          )}
                        </div>
                      </>
                    );
                  })}
                </div>

                <p className="text-xs text-muted-foreground font-inter mt-3">
                  Enabling Edit automatically enables View. Permissions merge across multiple roles — most permissive wins.
                </p>
              </div>

              <div className="px-5 py-4 border-t border-border flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setPermissionsRole(null)}
                  disabled={permissionsSaving}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePermissions}
                  disabled={permissionsSaving}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
                >
                  {permissionsSaving ? "Saving…" : "Save permissions"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}
